import { onUnmounted } from "vue";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";

/**
 * Manages the global splash screen for pages that need to load async data
 * before revealing the UI (e.g. multi-step forms, pages with steppers).
 *
 * Usage:
 *   const { showSplash, hideSplash } = useSplashWhileLoading();
 *
 *   onMounted(async () => {
 *     showSplash();
 *     try {
 *       await Promise.all([...api calls...]);
 *       // populate form / initialize stepper
 *     } finally {
 *       hideSplash(); // always runs, even on error
 *     }
 *   });
 *
 * The splash is also automatically removed if the component is destroyed
 * before hideSplash() is called (e.g. user navigates away during loading).
 */

// Module-level counter: tracks how many components have requested the splash.
// This prevents App.vue's router.isReady() callback from removing the splash
// while a component's API calls are still in progress (race condition on refresh).
let splashCount = 0;

export function isSplashActive(): boolean {
  return splashCount > 0;
}

export function useSplashWhileLoading() {
  const store = useStore();

  const showSplash = () => {
    splashCount++;
    store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
  };

  const hideSplash = () => {
    if (splashCount > 0) splashCount--;
    if (splashCount === 0) {
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
    }
  };

  // Safety net: if the component unmounts before hideSplash() is called,
  // the splash is removed automatically (e.g. navigation during loading).
  onUnmounted(hideSplash);

  return { showSplash, hideSplash };
}
